/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { FunctionComponent } from 'react'

import {
  ITreeNode,
  useTreeProject,
  useRouter,
} from '@builddocs/data-management'
import TreeItem, {
  treeItemClasses,
  TreeItemContentProps,
  TreeItemProps,
  useTreeItem,
} from '@mui/lab/TreeItem'
import { Button, Skeleton, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import clsx from 'clsx'

const CustomContent = React.forwardRef(function CustomContent(
  props: TreeItemContentProps,
  ref,
) {
  const {
    classes,
    className,
    label,
    nodeId,
    icon: iconProp,
    expansionIcon,
    displayIcon,
    ...rest
  } = props

  const {
    disabled,
    expanded,
    selected,
    focused,
    handleExpansion,
    handleSelection,
    preventSelection,
  } = useTreeItem(nodeId)

  const icon = iconProp || expansionIcon || displayIcon

  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    preventSelection(event)
  }

  const handleExpansionClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    handleExpansion(event)
  }

  const handleSelectionClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    handleSelection(event)
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      {...rest}
      ref={ref as React.Ref<HTMLDivElement>}
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      title={label?.toString()}
      onMouseDown={handleMouseDown}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className={classes.iconContainer}
        style={{ color: '#000' }}
        onClick={handleExpansionClick}
      >
        {icon}
      </div>
      <Typography
        className={classes.label}
        component="div"
        onClick={handleSelectionClick}
      >
        {label}
      </Typography>
    </div>
  )
})

const CustomTreeItem = ({
  level = 0,
  ...props
}: TreeItemProps & { level?: number }) => {
  return (
    <TreeItem
      ContentComponent={CustomContent}
      style={{ marginLeft: level * 8 + 'px' }}
      {...props}
    />
  )
}

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
  position: 'relative',
  [`& .${treeItemClasses.content}`]: {
    color: '#0034AD',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(1),

    '& .MuiTreeItem-label': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },

    //    '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
    //      backgroundColor: alpha(theme.palette.common.white, 0.7),
    //    },
    //    '&.Mui-focused': {
    //      backgroundColor: 'transparent',
    //    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 0,
    //    paddingLeft: 2,
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: theme.spacing(2),
    },
  },
}))

const RenderNestedContracts = (props: {
  readonly contractNode: ITreeNode
  readonly projectId: string
  readonly contractId: string
  readonly level: number
}) => {
  const { contractNode, projectId, level = 0 } = props

  const { journalId, title } = contractNode.self

  return (
    <StyledTreeItem
      key={journalId}
      label={title}
      level={level}
      nodeId={`${projectId}:${journalId}`}
    >
      {contractNode?.children?.map(subContractNode => (
        <RenderNestedContracts
          key={subContractNode.self.journalId}
          contractId={subContractNode.self.journalId}
          contractNode={subContractNode}
          level={level + 1}
          projectId={projectId}
        />
      )) ?? null}
    </StyledTreeItem>
  )
}

interface OwnProps {
  projectId: string
}

type Props = OwnProps

const TreeContracts: FunctionComponent<Props> = props => {
  const { projectId } = props
  const { navigate } = useRouter()
  const { projectTree, isLoading } = useTreeProject(projectId, undefined)

  if (isLoading) return <Skeleton height={24} variant="rectangular" />
  const tryToCreateContract = () => {
    const code = window.prompt('Введите код. Его можно запросить в поддержке.')

    if (code === '1313132018') {
      navigate(`/projects/${projectId}/contracts/create`)
    }
  }

  if (!projectTree?.journalNodes || !projectTree.journalNodes.length)
    return (
      <Stack alignItems="center" my={3} spacing={2}>
        <Typography align="center">Договоров нет</Typography>
        <Button variant="contained" onClick={() => tryToCreateContract()}>
          Новый договор
        </Button>
      </Stack>
    )

  return (
    <>
      {projectTree?.journalNodes?.map(contractNode => (
        <StyledTreeItem
          key={contractNode.self.journalId}
          label={contractNode.self.title}
          nodeId={`${projectId}:${contractNode.self.journalId}`}
        >
          {contractNode.children?.map(subContractNode => (
            <RenderNestedContracts
              key={subContractNode.self.journalId}
              contractId={subContractNode.self.journalId}
              contractNode={subContractNode}
              level={1}
              projectId={projectId}
            />
          ))}
        </StyledTreeItem>
      ))}
    </>
  )
}

export default TreeContracts
